import { Tabs, TabsContent, TabsList, TabsTrigger } from "@workspace/web-ui/components/tabs";
import { MedicalPlan } from "../tab-content/medical-plan";
import { Medication } from "../tab-content/medication";
import { PaymentHistory } from "../tab-content/payment-history";
import { Questionnaire } from "../tab-content/questionnaire";
import { Timeline } from "../tab-content/timeline";
import { Verification } from "../tab-content/verification";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  patientDetailsQueryOptions,
  patientMedicalPlanQueryOptions,
  patientMedicationQueryOptions,
  patientNotesQueryOptions,
  patientPaymentHistoryQueryOptions,
  patientQuestionnaireQueryOptions,
  patientTimelineQueryOptions,
  patientVerificationQueryOptions,
} from "../../api/queries";
import {
  TimelineSkeleton,
  MedicationSkeleton,
  MedicalPlanSkeleton,
  QuestionnaireSkeleton,
  PaymentHistorySkeleton,
  NotesSkeleton,
  VerificationSkeleton,
} from "../skeletons/tab-content-skeletons";
import { useState } from "react";
import NotesCard from "../card/notes-card";
import { Overview } from "../tab-content/overview";

type PatientLogTabsProps = {
  userId: string;
  program: string;
};

export const TAB_CONFIG = [
  { value: "overview", label: "Overview" },
  { value: "timeline", label: "Timeline" },
  { value: "medication", label: "Medication" },
  { value: "questionnaire", label: "Questionnaire" },
  // { value: "medical-plan", label: "Medical Plan" },
  { value: "payment-history", label: "Payment History" },
  { value: "notes", label: "Notes" },
  { value: "verification", label: "Verification" },
] as const;

export type TabValue = typeof TAB_CONFIG[number]["value"];

export const PatientLogTabs = ({ userId, program}: PatientLogTabsProps) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("overview");
  const { data: patientData, isLoading: isPatientLoading } = useQuery(
    patientDetailsQueryOptions(userId, program)
  );

  const { data: timelineData, isLoading: isTimelineLoading } = useQuery(
    patientTimelineQueryOptions(userId)
  );

  const { data: prescribedMedicationData, isLoading: isPrescribedMedicationLoading } = useQuery(
    patientMedicationQueryOptions(userId, program, "prescribed")
  );

  const { data: activeMedicationData, isLoading: isActiveMedicationLoading } = useQuery(
    patientMedicationQueryOptions(userId, program, "active")
  );

  const { data: requestsMedicationData, isLoading: isRequestsMedicationLoading } = useQuery(
    patientMedicationQueryOptions(userId, program, "requests")
  );

  const { data: historyMedicationData, isLoading: isHistoryMedicationLoading } = useQuery(
    patientMedicationQueryOptions(userId, program, "historic")
  );

  const { data: medicalPlanData, isLoading: isMedicalPlanLoading } = useQuery(
    patientMedicalPlanQueryOptions(userId, program)
  );

  const { data: questionnaireData, isLoading: isQuestionnaireLoading } = useQuery(
    patientQuestionnaireQueryOptions(userId, program)
  );

  const { data: paymentHistoryData, isLoading: isPaymentHistoryLoading } = useQuery(
    patientPaymentHistoryQueryOptions(userId, program)
  );

  const { data: notesData, isLoading: isNotesLoading } = useQuery(
    patientNotesQueryOptions(userId)
  );

  const { data: verificationData, isLoading: isVerificationLoading } = useQuery(
    patientVerificationQueryOptions(userId)
  );

  const prefetchTabData = (tabValue: string): void => {
    if (!userId) return;

    switch (tabValue) {
      case "overview":
        queryClient.prefetchQuery(patientMedicalPlanQueryOptions(userId, program));
        break;
      case "timeline":
        queryClient.prefetchQuery(patientTimelineQueryOptions(userId));
        break;
      case "medication":
        queryClient.prefetchQuery(patientMedicationQueryOptions(userId, program, "active"));
        queryClient.prefetchQuery(patientMedicationQueryOptions(userId, program, "prescribed"));
        queryClient.prefetchQuery(patientMedicationQueryOptions(userId, program, "requests"));
        queryClient.prefetchQuery(patientMedicationQueryOptions(userId, program, "historic"));
        break;
      case "medical-plan":
        queryClient.prefetchQuery(patientMedicalPlanQueryOptions(userId, program));
        break;
      case "payment-history":
        queryClient.prefetchQuery(patientPaymentHistoryQueryOptions(userId, program));
        break;
      case "notes":
        queryClient.prefetchQuery(patientNotesQueryOptions(userId));
        break;
      case "verification":
        queryClient.prefetchQuery(patientVerificationQueryOptions(userId));
        break;
    }
  };

  const handleTabChange = (value: string): void => {
    setActiveTab(value);
  };

  return (
    <Tabs defaultValue="overview" value={activeTab} className="w-full" onValueChange={handleTabChange}>
      <div className="w-full flex overflow-x-auto pb-1 sm:pb-2 hide-scrollbar scrollbar-hide">
        <TabsList className="flex border-b no-scrollbar bg-transparent px-0 py-0 sm:py-1 whitespace-nowrap hide-scrollbar scrollbar-hide">
          {TAB_CONFIG.map(({ value, label }) => (
            <TabsTrigger
              key={value}
              value={value}
              className={`
                tab-trigger flex-shrink-0 text-xs sm:text-sm md:text-base whitespace-nowrap py-2
                ${activeTab === value ? "font-medium" : "font-normal"}
              `}
              onMouseEnter={() => prefetchTabData(value)}
            >
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
      </div>
      <div className="w-full mt-4">
        <TabsContent value="overview">
          {isMedicalPlanLoading || isPatientLoading || !patientData?.userId ? (
            <TimelineSkeleton />
          ) : (
            <Overview medicalPlanData={medicalPlanData}  userId={patientData?.userId} encounterId={activeMedicationData?.medical_plan?.encounter?.encounter_id || ""} />
          )}
        </TabsContent>
        <TabsContent value="timeline">
          {isTimelineLoading ? (
            <TimelineSkeleton />
          ) : (
            <Timeline timelineData={timelineData} />
          )}
        </TabsContent>
        <TabsContent value="medication">
          {isActiveMedicationLoading || isRequestsMedicationLoading || isHistoryMedicationLoading || isPrescribedMedicationLoading || isPatientLoading ? (
            <MedicationSkeleton />
          ) : (
            patientData && medicalPlanData && (
              <Medication
                activeMedicationData={activeMedicationData}
                prescribedMedicationData={prescribedMedicationData}
                requestsMedicationData={requestsMedicationData}
                historyMedicationData={historyMedicationData}
                medicalPlanData={medicalPlanData}
                patient={medicalPlanData}
                userId={patientData.userId}
                patientData={patientData}
                program={program}
              />
            )
          )}
        </TabsContent>
        <TabsContent value="questionnaire">
          {isQuestionnaireLoading ? (
            <QuestionnaireSkeleton />
          ) : (
            <Questionnaire questionnaireData={questionnaireData ?? null} />
          )}
        </TabsContent>
        <TabsContent value="medical-plan">
          {isMedicalPlanLoading ? (
            <MedicalPlanSkeleton />
          ) : (
            <MedicalPlan medicalPlanData={medicalPlanData} userId={userId} />
          )}
        </TabsContent>
        <TabsContent value="payment-history">
          {isPaymentHistoryLoading ? (
            <PaymentHistorySkeleton />
          ) : (
            <PaymentHistory paymentHistoryData={paymentHistoryData} />
          )}
        </TabsContent>
        <TabsContent value="notes">
          {isNotesLoading ? (
            <NotesSkeleton />
          ) : (
            <NotesCard notesDataEncounter={notesData ?? null} />
          )}
        </TabsContent>
        <TabsContent value="verification">
          {isVerificationLoading ? (
            <VerificationSkeleton />
          ) : (
            <Verification verificationData={verificationData ?? null} userId={userId} />
          )}
        </TabsContent>
      </div>
    </Tabs>
  );
};
